
import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';
import { FeatureCollection } from 'geojson';
import { cleanupLayers } from './utils/layer-cleanup';
import { initializeSource } from './utils/source-management';
import { createRegionBorderLayer, createSelectionLayer } from './styles/layer-styles';
import { useLegendFilter } from '../../../contexts/legend-filter/LegendFilterContext';
import { addVectorLayers, waitForStyleLoad } from './utils/vector-layers';

interface MapLayersProps {
  map: mapboxgl.Map;
  geoJsonData: FeatureCollection;
  propertyKey: string;
  regions: Region[];
  selectedRegion: Region | null;
  showGlobalRegions?: boolean;
}

const MapLayers: React.FC<MapLayersProps> = ({ 
  map, 
  geoJsonData, 
  propertyKey,
  regions,
  selectedRegion,
  showGlobalRegions = false
}) => {
  const { activeFilter } = useLegendFilter();
  
  useEffect(() => {
    if (!map || !geoJsonData) return;
    
    console.log("MapLayers: Adding layers with selectedRegion:", selectedRegion?.english_name || "none");
    console.log("MapLayers: Using propertyKey:", propertyKey);
    console.log("MapLayers: Active filter:", activeFilter);
    console.log("MapLayers: Showing global regions:", showGlobalRegions);
    
    // First, remove all existing layers including region fills
    cleanupLayers(map);
    
    // Only show vector layers for Mexico view, not global view
    if (!showGlobalRegions) {
      // Wait for style to load then add vector layers
      waitForStyleLoad(map).then(() => {
        console.log("Map style loaded, adding vector layers");
        addVectorLayers(map);
        
        // Add selection layer if a region is selected
        if (selectedRegion) {
          map.addLayer(createSelectionLayer(selectedRegion.region_id));
        }
      });
    } else {
      // Initialize with GeoJSON for global view
      initializeSource(map, geoJsonData);
      
      // Add the base fill layer with direct church presence coloring
      map.addLayer({
        id: 'region-fills',
        type: 'fill',
        source: 'regions',
        layout: {},
        paint: {
          'fill-color': [
            'match',
            ['get', 'church_presence'],
            'Good', '#0000FF',     // Blue for good presence
            'Low', '#FFC0CB',      // Pink for low presence
            'Unreached', '#FF4C4C', // Red for unreached areas
            '#CCCCCC'  // Default gray for unknown
          ],
          'fill-opacity': 0.6
        }
      });
      
      // Add special styling for Low and Unreached regions
      map.addLayer({
        id: 'special-regions-border',
        type: 'line',
        source: 'regions',
        filter: ['any', 
          ['==', ['get', 'church_presence'], 'Low'],
          ['==', ['get', 'church_presence'], 'Unreached']
        ],
        paint: {
          'line-color': [
            'match',
            ['get', 'church_presence'],
            'Low', '#9333ea',      // Purple for Low presence regions
            'Unreached', '#f97316', // Orange for Unreached regions
            '#ffffff'              // Default white
          ],
          'line-width': 3,
          'line-opacity': 0.8
        }
      });
      
      // Add border layer
      map.addLayer(createRegionBorderLayer());
    }
    
    return () => {
      cleanupLayers(map);
    };
  }, [map, geoJsonData, selectedRegion, activeFilter, showGlobalRegions]);

  return null;
};

export default MapLayers;
